/**
 * Token opaque local pour reprendre une session.
 *
 * Contraintes RGPD (cf. RGPD_PLAN §2.1) :
 * - Stockage UNIQUEMENT dans `localStorage` (jamais cookie, jamais envoyé
 *   par e-mail ni QR-code partagé).
 * - Aucune PII : pas d'e-mail, pas de prénom, pas d'IP. Seul un identifiant
 *   opaque (sessionId UUID retourné par le serveur) est conservé.
 * - Clé namespacée par slug d'enquête → si l'utilisateur ouvre une autre
 *   enquête sur le même appareil, aucune collision.
 * - Le serveur, de son côté, hash l'identifiant et ne stocke pas l'IP/UA
 *   (RGPD_PLAN §0).
 *
 * Le token n'est pas chiffré côté client : c'est un identifiant opaque, sans
 * valeur en dehors du couple (cet appareil, ce serveur). En cas de vol du
 * device, l'attaquant accède aux réponses en cours — c'est le compromis
 * accepté pour permettre la reprise sans identifier le répondant.
 */

const PREFIX = 'wfs_resume:';

/** Crée la clé localStorage pour un slug d'enquête. */
function keyFor(surveyId: string): string {
  // Pas de hashage : la clé n'est pas un secret, c'est juste un namespace.
  // Le surveyId est lui-même public.
  return `${PREFIX}${surveyId}`;
}

/** Stocke le token opaque pour reprendre. */
export function storeResumeToken(surveyId: string, sessionId: string): void {
  try {
    window.localStorage.setItem(keyFor(surveyId), sessionId);
  } catch {
    /* Storage désactivé (mode privé / quota dépassé) : reprise indisponible,
     * mais le parcours reste fonctionnel pour cette session. */
  }
}

/** Récupère le token s'il existe. */
export function readResumeToken(surveyId: string): string | null {
  try {
    return window.localStorage.getItem(keyFor(surveyId));
  } catch {
    return null;
  }
}

/** Supprime le token (après soumission ou refus de consentement). */
export function clearResumeToken(surveyId: string): void {
  try {
    window.localStorage.removeItem(keyFor(surveyId));
  } catch {
    /* no-op */
  }
}

/**
 * Purge tous les tokens de reprise de localStorage (utile pour le bouton
 * « effacer mes données locales » côté répondant).
 */
export function clearAllResumeTokens(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* no-op */
  }
}
